/* ==========================================================================
   Real people, real spaces — an endless row of story cards.

   Loop: the cards are copied a few times (copies are hidden from screen
   readers and keyboard) and the row starts on the middle copy. Whenever it
   comes to rest near either end it jumps, invisibly, by exactly one copy's
   width, so there's always more row in both directions.

   Drag: touch scrolls natively. A mouse can grab and drag the row too, with
   a little fling on release, then it settles on the nearest card. A drag
   never counts as a click on a card's link.

   Card videos play only while on screen, and never for visitors who ask for
   reduced motion.
   ========================================================================== */
(function () {
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (root.dataset.fbReady) return;
    var track = root.querySelector('[data-fb-rs-track]');
    if (!track) return;
    var originals = Array.prototype.slice.call(track.querySelectorAll('.fb-rs-card'));
    if (!originals.length) return;
    root.dataset.fbReady = '1';

    var prev = root.querySelector('[data-fb-rs-prev]');
    var next = root.querySelector('[data-fb-rs-next]');
    var arrows = root.querySelector('.fb-rs-arrows');

    /* ---------------- Copies for the loop ---------------- */
    var copies = 1;
    var setWidth = 0;
    var firstOf = function (n) { return track.children[n * originals.length]; };
    var startOf = function (el) { return el.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0); };

    var build = function () {
      // Remove old copies, then add enough on each side that no single swipe
      // can reach an end: phones can't be moved mid-fling without killing the
      // momentum, so the row only re-centres once a swipe stops, and a hard
      // flick on a phone easily covers several thousand pixels.
      Array.prototype.slice.call(track.querySelectorAll('[data-fb-rs-clone]')).forEach(function (c) { c.remove(); });
      var gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      var oneSet = originals.reduce(function (w, c) { return w + c.getBoundingClientRect().width + gap; }, 0);
      if (oneSet <= 0) return false;
      var perSide = Math.max(2, Math.ceil(Math.max(10000, track.clientWidth * 4) / oneSet));
      copies = perSide * 2 + 1;
      var makeCopy = function () {
        var frag = document.createDocumentFragment();
        originals.forEach(function (card) {
          var c = card.cloneNode(true);
          c.setAttribute('data-fb-rs-clone', '');
          c.setAttribute('aria-hidden', 'true');
          c.setAttribute('tabindex', '-1');
          c.inert = true;
          // The theme editor tracks blocks by these; copies mustn't claim them.
          Array.prototype.slice.call(c.attributes).forEach(function (a) {
            if (a.name.indexOf('data-shopify') === 0) c.removeAttribute(a.name);
          });
          frag.appendChild(c);
        });
        return frag;
      };
      for (var i = 0; i < perSide; i++) {
        track.insertBefore(makeCopy(), track.firstChild);
        track.appendChild(makeCopy());
      }
      setWidth = startOf(firstOf(perSide + 1)) - startOf(firstOf(perSide));
      jumpTo(startOf(firstOf(perSide)));
      return true;
    };

    // Instant scroll, with snapping briefly off so the browser doesn't fight it.
    var jumpTo = function (left) {
      track.classList.add('is-jumping');
      track.scrollLeft = left;
      void track.offsetWidth;
      track.classList.remove('is-jumping');
    };

    // Bring the view back to the middle copy, by however many whole copies
    // it has travelled; returns how far it moved.
    var wrap = function () {
      if (!setWidth) return 0;
      var middle = startOf(firstOf(Math.floor(copies / 2)));
      var shift = Math.round((middle - track.scrollLeft) / setWidth) * setWidth;
      if (shift) jumpTo(track.scrollLeft + shift);
      return shift;
    };

    /* ---------------- Settling ---------------- */
    // Wrap only once scrolling has stopped, so a smooth scroll or a touch
    // fling is never cut short. scrollend where supported, else a pause.
    var settleTimer = null;
    var onSettle = function () { if (!dragging) wrap(); };
    if ('onscrollend' in window) track.addEventListener('scrollend', onSettle);
    track.addEventListener('scroll', function () {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettle, 140);
    }, { passive: true });

    /* ---------------- Arrows ---------------- */
    var cardStep = function () {
      var gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      return originals[0].getBoundingClientRect().width + gap;
    };
    // Re-centre on the middle copy before each step, so fast repeated clicks
    // can never run into either end, even before the last scroll has settled.
    var stepBy = function (dir) {
      wrap();
      track.scrollBy({ left: dir * cardStep(), behavior: reduceMotion ? 'auto' : 'smooth' });
    };
    if (prev) { prev.disabled = false; prev.addEventListener('click', function () { stepBy(-1); }); }
    if (next) { next.disabled = false; next.addEventListener('click', function () { stepBy(1); }); }

    /* ---------------- Mouse drag ---------------- */
    var dragging = false, moved = false, startX = 0, startScroll = 0, lastX = 0, lastT = 0, velocity = 0, raf = null;

    var nearestCard = function (left) {
      var step = cardStep();
      var base = startOf(track.children[0]);
      return base + Math.round((left - base) / step) * step;
    };
    var release = function () {
      // Carry on in the drag direction, up to three cards for a hard flick.
      var step = cardStep();
      var fling = Math.max(-3 * step, Math.min(3 * step, velocity * 220));
      var target = nearestCard(track.scrollLeft - fling);
      track.classList.remove('is-dragging');
      track.classList.add('is-gliding');
      track.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
      clearTimeout(release.t);
      release.t = setTimeout(function () { track.classList.remove('is-gliding'); wrap(); }, reduceMotion ? 0 : 600);
    };

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragging = true; moved = false;
      startX = lastX = e.clientX; lastT = e.timeStamp; velocity = 0;
      startScroll = track.scrollLeft;
      if (raf) cancelAnimationFrame(raf);
    });
    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < 5) return;
      if (!moved) { moved = true; track.classList.add('is-dragging'); }
      e.preventDefault();
      track.scrollLeft = startScroll - dx;
      // Crossing a copy boundary mid-drag: move the reference point with it.
      startScroll += wrap();
      var dt = Math.max(1, e.timeStamp - lastT);
      velocity = 0.8 * ((e.clientX - lastX) / dt) + 0.2 * velocity;
      lastX = e.clientX; lastT = e.timeStamp;
    });
    window.addEventListener('pointerup', function () {
      if (!dragging) return;
      dragging = false;
      if (moved) release();
    });
    // A drag that ends over a link must not open it.
    track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
    // Stop the browser's own image/link dragging from hijacking the gesture.
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    /* ---------------- Start ---------------- */
    var start = function () {
      if (!build()) return;
      if (arrows) arrows.style.visibility = '';
      setupVideos();
    };
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        // Card widths change with the screen, so rebuild the copies and keep
        // the same card in view.
        var step = cardStep();
        var index = step ? Math.round((track.scrollLeft - startOf(firstOf(Math.floor(copies / 2)))) / step) : 0;
        if (build()) jumpTo(track.scrollLeft + index * cardStep());
        setupVideos();
      }, 150);
    });

    /* ---------------- Videos ---------------- */
    var io = null;
    function setupVideos() {
      var videos = Array.prototype.slice.call(track.querySelectorAll('video'));
      videos.forEach(function (v) {
        // Forced here rather than trusted to the markup: browsers only
        // autoplay muted video, and without playsinline iOS goes fullscreen.
        v.muted = true;
        v.playsInline = true;
        v.removeAttribute('autoplay');
        v.pause();
      });
      if (io) io.disconnect();
      if (!videos.length || reduceMotion || !('IntersectionObserver' in window)) return;
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.4 });
      videos.forEach(function (v) { io.observe(v); });
    }

    // Card widths depend on fonts and images being laid out.
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  function initAll() { document.querySelectorAll('[data-fb-real-spaces]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  // Re-run when the section is added or changed in the theme editor.
  document.addEventListener('shopify:section:load', initAll);
})();
